import React, { Fragment, ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import { IGif, INicheMap } from "services/types";
import { PreviewFeed } from "components/feed/PreviewFeed";
import { TrendingCreators, TrendingNiches, TrendingTags } from "components/widgets";
import { useHomeFeedQuery } from "queries";

import "./HomeFeed.scss";
import { filterGifsList } from "utils/filterGifsList";
import { filterNichesList } from "utils/filterNichesList";
import { IUserList } from "components/feed/GifList";
import { filterUsersList } from "utils/filterUsersList";
import { ViewRegion } from "utils/metrics";
import { FeedAds } from "components/ads/FeedAds";
import { MobileHomePageScrollKeys } from "components/ads/config/units/homepageScrollMobile";
import { DesktopHomePageScrollKeys } from "components/ads/config/units/homepageScrollDesktop";

const CREATORS_POSITION = 3;
const NICHES_POSITION = 8;

export const HomeFeed = () => {
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>(1);
  const [gifs, setGifs] = useState<IGif[]>([]);
  const [users, setUsers] = useState<IUserList>({});
  const [niches, setNiches] = useState<INicheMap>({});

  const { data, isSuccess, isError } = useHomeFeedQuery({
    page: page,
    count: 10,
  });

  useEffect(() => {
    if (isSuccess && data !== undefined) {
      setGifs(prev => filterGifsList(prev, data.gifs));
      setPages(data.pages);
      setUsers(prev => filterUsersList(prev, data.users));

      if (data.niches) {
        setNiches(prev => filterNichesList(prev, data.niches));
      }
    }
  }, [data, isSuccess]);

  const hasMore = useMemo(() => page < pages, [page, pages]);

  const fetchMore = useCallback(() => {
    setPage((prevPage) => prevPage + 1);
  }, []);

  const injector = useCallback((pos: number, gif: IGif, fullscreen: boolean): JSX.Element => {
    const children: ReactNode[] = [];

    if (pos === CREATORS_POSITION && !fullscreen) {
      children.push(<TrendingCreators key="TrendingCreators" />);
    }

    if (pos === NICHES_POSITION && !fullscreen) {
      children.push(<TrendingNiches key="TrendingNiches" />);
    }

    children.push(
      <FeedAds
        position={pos}
        fullscreen={fullscreen}
        mobileAds={MobileHomePageScrollKeys}
        desktopAds={DesktopHomePageScrollKeys}
      />
    );

    return <Fragment>{children}</Fragment>;
  }, []);

  return (
    <div className="homeFeed">
      <TrendingTags />

      {isError && <p className="error">Error loading gifs. Please refresh the page.</p>}

      <PreviewFeed
        gifs={gifs}
        users={users}
        niches={niches}
        hasMore={hasMore}
        fetchMore={fetchMore}
        injector={injector}
        source={ViewRegion.Main}
      />
    </div>
  );
};
